import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import AnimatedPage from '../components/AnimatedPage';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { 
  Clock,
  Users,
  CheckCircle2
} from 'lucide-react';

const Mindfulness = () => {
  const [detailsRef, detailsVisible] = useScrollAnimation(0.1);

  return (
    <AnimatedPage className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-amber-200 to-orange-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
            Mindfulness
          </h1>
          <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 mx-auto mb-8"></div>
          
          <p className="text-xl text-stone-700 mb-6 leading-relaxed">
            Experience peace, clarity, and inner calm in a supportive and welcoming environment.
          </p>
        </div>
      </section>

      {/* Guided Meditation Sessions Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="glass-card-strong shadow-strong card-premium border-stone-200">
            <CardContent className="p-10">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-stone-900 mb-4">
                  Guided Meditation Sessions
                </h2>
                <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 mx-auto mb-6"></div>
              </div>
              
              <div className="space-y-4 text-stone-700 leading-relaxed text-center">
                <p>
                  Discover practical techniques that promote relaxation, mindfulness, and overall wellness. 
                  These sessions provide a calm and supportive space where participants can learn to quiet 
                  the mind, manage stress, and develop healthy habits for long-term well-being.
                </p>
                <p className="font-medium text-stone-800">
                  Whether you are new to meditation or have previous experience, all are welcome.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Session Details Section */}
      <section ref={detailsRef} className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`transition-all duration-1000 ${detailsVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <Card className="glass-card-strong shadow-strong card-premium bg-gradient-to-br from-amber-50/50 to-stone-50">
              <CardContent className="p-10">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-stone-900 mb-4">
                    Class Details
                  </h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-amber-500 via-amber-500 to-orange-500 mx-auto"></div>
                </div>
                
                <div className="space-y-4 max-w-2xl mx-auto">
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                    <p className="text-stone-700">
                      <span className="font-semibold text-stone-900">Duration:</span> 45 Minutes
                    </p>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                    <p className="text-stone-700 font-medium">Beginner Friendly</p>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                    <p className="text-stone-700 font-medium">Guided Instruction Included</p>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                    <p className="text-stone-700 font-medium">Comfortable and Supportive Environment</p>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-stone-200">
                    <p className="text-stone-600 italic text-center">
                      Please bring a yoga mat and/or a comfortable pillow
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* QR Code Section */}
      <section className="py-16 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-8">
            {/* Text on Left */}
            <p className="text-xl text-stone-700 leading-relaxed">
              Scan the QR code to know more about Mindfulness & Guided Meditation
            </p>
            
            {/* QR Code on Right */}
            <div className="flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/zqdxl0ka_Call_Karen_QR.png"
                alt="Mindfulness Services QR Code"
                className="w-32 h-32 object-contain"
              />
            </div>
          </div>
        </div>
      </section>
    </AnimatedPage>
  );
};

export default Mindfulness;
